/**
 * NetworkAdaptiveService - 根据网络质量自适应调整 WebRTC 参数
 *
 * 功能:
 * 1. 监控网络质量指标 (RTT, 丢包率, jitter)
 * 2. 解析 SDP 支持的功能 (NACK, RTX, FEC, RED)
 * 3. 根据网络状况动态调整: 码率、帧率、FEC
 * 4. 触发 ICE Restart 恢复断开的连接
 */

import { eventBus } from './EventBus';
import type { StreamStats } from '../types';

// 网络质量等级
export enum NetworkQuality {
  EXCELLENT = 'excellent',  // RTT < 50ms, 丢包 < 1%
  GOOD = 'good',           // RTT < 100ms, 丢包 < 3%
  FAIR = 'fair',           // RTT < 200ms, 丢包 < 5%
  POOR = 'poor',           // RTT < 500ms, 丢包 < 10%
  BAD = 'bad',             // RTT >= 500ms 或 丢包 >= 10%
}

// SDP 支持的功能
export interface SdpCapabilities {
  nack: boolean;      // 丢包重传请求
  rtx: boolean;       // 重传流
  fec: boolean;       // 前向纠错 (RED/UlpFEC)
  flexfec: boolean;   // FlexFEC
  twcc: boolean;      // 传输层拥塞控制
  remb: boolean;      // 接收端带宽估计
}

// 自适应参数配置
export interface AdaptiveConfig {
  fps: number;
  maxBitrate: number;
  minBitrate: number;
  fecEnabled: boolean;
  qualityPreset: 'low' | 'medium' | 'high' | 'auto';
}

// host 端 fps 上限（与 webrtc_host BitrateConstants::kHostMaxFps 对齐）
// 任何档位的 fps 都不能超过这个值，否则 host clamp 后会与前端配置不一致。
// 改这个常量时要同步改 webrtc_host/include/bitrate_controller.h 的 kHostMaxFps。
export const HOST_MAX_FPS = 30;

// 网络质量阈值
const QUALITY_THRESHOLDS = {
  excellent: { maxRtt: 50, maxLoss: 1, maxJitter: 10 },
  good: { maxRtt: 100, maxLoss: 3, maxJitter: 20 },
  fair: { maxRtt: 200, maxLoss: 5, maxJitter: 50 },
  poor: { maxRtt: 500, maxLoss: 10, maxJitter: 100 },
};

// 各质量等级对应的参数
// 注意：所有 fps 字段都用 Math.min(配置值, HOST_MAX_FPS) 包裹，确保不超过 host 上限。
// host 端 WebRTCHost::SetTargetFps 也会再 clamp 一次（防御性双保险）。
const QUALITY_CONFIGS: Record<NetworkQuality, AdaptiveConfig> = {
  [NetworkQuality.EXCELLENT]: {
    // 历史变迁: 60 → 50 (Stage 3B) → 30 (与 host kHostMaxFps 统一上限收口)
    // 选项 A（NVENC enableEncodeAsync）见 docs/NVENC_ASYNC_OPTIMIZATION.md
    fps: Math.min(50, HOST_MAX_FPS),
    maxBitrate: 8000,
    minBitrate: 2000,
    fecEnabled: false,  // 网络好时不需要 FEC
    qualityPreset: 'high',
  },
  [NetworkQuality.GOOD]: {
    fps: Math.min(50, HOST_MAX_FPS),
    maxBitrate: 6000,
    minBitrate: 1500,
    fecEnabled: false,
    qualityPreset: 'high',
  },
  [NetworkQuality.FAIR]: {
    fps: Math.min(30, HOST_MAX_FPS),
    maxBitrate: 4000,
    minBitrate: 1000,
    fecEnabled: true,   // 开启 FEC 应对丢包
    qualityPreset: 'medium',
  },
  [NetworkQuality.POOR]: {
    fps: Math.min(20, HOST_MAX_FPS),
    maxBitrate: 2000,
    minBitrate: 500,
    fecEnabled: true,
    qualityPreset: 'low',
  },
  [NetworkQuality.BAD]: {
    fps: Math.min(10, HOST_MAX_FPS),
    maxBitrate: 1000,
    minBitrate: 300,
    fecEnabled: true,
    qualityPreset: 'low',
  },
};

class NetworkAdaptiveService {
  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private isEnabled = true;
  private checkInterval: number | null = null;

  // 当前状态
  private currentQuality: NetworkQuality = NetworkQuality.GOOD;
  private sdpCapabilities: SdpCapabilities | null = null;
  private lastConfig: AdaptiveConfig | null = null;

  // 统计数据历史 (用于平滑)
  private statsHistory: {
    rtt: number[];
    packetLoss: number[];
    jitter: number[];
  } = {
    rtt: [],
    packetLoss: [],
    jitter: [],
  };

  // ICE 状态监控
  private iceDisconnectTime = 0;
  private readonly ICE_RESTART_THRESHOLD_MS = 5000; // 5秒后触发 ICE Restart

  // 配置变更的最小间隔
  private lastConfigChangeTime = 0;
  private readonly CONFIG_CHANGE_COOLDOWN_MS = 3000; // 3秒冷却

  // [DEBUG-PROBE TODO: delete] [VideoConfigSent] 探针 C 状态：跟踪 video_config 下发节奏
  // 调查结论：前端 video_config 下发节奏与 host 14s 码率峰无关；日后清理时连同两处 console.log 一起删
  private lastVideoConfigSentMs = 0;
  private videoConfigSentCount = 0;

  // ===== fps 平滑（快升慢降）=====
  // 启动时 null，首次 evaluate 后 = targetFps（直接采用，不慢升）
  // targetFps >= smoothedFps 时立即跟随；< 时每 FPS_DOWN_INTERVAL_MS 降 FPS_DOWN_STEP
  // 与 CONFIG_CHANGE_COOLDOWN 叠加为两层防抖（quality 切换冷却 + fps 平滑）
  private smoothedFps: number | null = null;
  private lastFpsEvalMs = 0;
  private lastSentFps = 0;  // 上次实际下发的 fps，用于去重
  private readonly FPS_DOWN_STEP = 1;            // 每秒降 1 fps（30→29→28→27→...）
  private readonly FPS_DOWN_INTERVAL_MS = 1000;  // 至少 1 秒间隔降一次

  /**
   * 启动网络自适应监控
   */
  start(pc: RTCPeerConnection, dataChannel: RTCDataChannel): void {
    this.stop();
    this.pc = pc;
    this.dataChannel = dataChannel;

    // 解析 SDP 能力
    this.parseSdpCapabilities();

    // 监听统计数据更新
    eventBus.on('stats:update', this.handleStatsUpdate.bind(this));

    // 监听 ICE 状态变化
    pc.oniceconnectionstatechange = () => this.handleIceStateChange();

    // 定期检查网络质量
    this.checkInterval = window.setInterval(() => this.checkNetworkQuality(), 1000);

  }

  /**
   * 停止监控
   */
  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    eventBus.off('stats:update', this.handleStatsUpdate.bind(this));
    this.pc = null;
    this.dataChannel = null;
    this.statsHistory = { rtt: [], packetLoss: [], jitter: [] };
  }

  /**
   * 解析 SDP 获取支持的能力
   */
  private parseSdpCapabilities(): void {
    if (!this.pc) return;

    const localDesc = this.pc.localDescription;
    const remoteDesc = this.pc.remoteDescription;

    const capabilities: SdpCapabilities = {
      nack: false,
      rtx: false,
      fec: false,
      flexfec: false,
      twcc: false,
      remb: false,
    };

    const checkSdp = (sdp: string) => {
      // NACK 支持
      if (sdp.includes('nack') || sdp.includes('a=rtcp-fb:') && sdp.includes('nack')) {
        capabilities.nack = true;
      }

      // RTX 支持 (重传流)
      if (sdp.includes('rtx/90000') || sdp.includes('apt=')) {
        capabilities.rtx = true;
      }

      // FEC 支持 (RED + UlpFEC)
      if (sdp.includes('red/90000') || sdp.includes('ulpfec/90000')) {
        capabilities.fec = true;
      }

      // FlexFEC 支持
      if (sdp.includes('flexfec')) {
        capabilities.flexfec = true;
      }

      // TWCC 支持
      if (sdp.includes('transport-cc') || sdp.includes('transport-wide-cc')) {
        capabilities.twcc = true;
      }

      // REMB 支持
      if (sdp.includes('goog-remb') || sdp.includes('remb')) {
        capabilities.remb = true;
      }
    };

    if (localDesc?.sdp) checkSdp(localDesc.sdp);
    if (remoteDesc?.sdp) checkSdp(remoteDesc.sdp);

    this.sdpCapabilities = capabilities;
  }

  /**
   * 处理统计数据更新
   */
  private handleStatsUpdate(stats: StreamStats): void {
    // 记录历史数据 (保留最近 10 个样本)
    const maxHistory = 10;

    if (stats.rtt !== undefined && stats.rtt > 0) {
      this.statsHistory.rtt.push(stats.rtt);
      if (this.statsHistory.rtt.length > maxHistory) {
        this.statsHistory.rtt.shift();
      }
    }

    if (stats.packetLoss !== undefined) {
      this.statsHistory.packetLoss.push(stats.packetLoss);
      if (this.statsHistory.packetLoss.length > maxHistory) {
        this.statsHistory.packetLoss.shift();
      }
    }

    if (stats.jitterBuffer !== undefined && stats.jitterBuffer > 0) {
      this.statsHistory.jitter.push(stats.jitterBuffer);
      if (this.statsHistory.jitter.length > maxHistory) {
        this.statsHistory.jitter.shift();
      }
    }
  }

  /**
   * 处理 ICE 状态变化
   */
  private handleIceStateChange(): void {
    if (!this.pc) return;

    const state = this.pc.iceConnectionState;

    if (state === 'disconnected') {
      this.iceDisconnectTime = Date.now();
    } else if (state === 'connected' || state === 'completed') {
      this.iceDisconnectTime = 0;
    } else if (state === 'failed') {
      // ICE 失败，尝试重启
      this.triggerIceRestart();
    }
  }

  /**
   * 检查网络质量并调整参数
   */
  private checkNetworkQuality(): void {
    if (!this.isEnabled || !this.pc || !this.dataChannel) return;
    if (this.dataChannel.readyState !== 'open') return;

    // 计算平均值
    const avgRtt = this.getAverage(this.statsHistory.rtt);
    const avgLoss = this.getAverage(this.statsHistory.packetLoss);
    const avgJitter = this.getAverage(this.statsHistory.jitter);

    // 判断网络质量
    const newQuality = this.evaluateQuality(avgRtt, avgLoss, avgJitter);

    // 检查是否需要 ICE Restart
    if (this.iceDisconnectTime > 0) {
      const disconnectDuration = Date.now() - this.iceDisconnectTime;
      if (disconnectDuration > this.ICE_RESTART_THRESHOLD_MS) {
        this.triggerIceRestart();
      }
    }

    // 如果质量等级变化，调整参数
    if (newQuality !== this.currentQuality) {
      const now = Date.now();
      if (now - this.lastConfigChangeTime > this.CONFIG_CHANGE_COOLDOWN_MS) {

        this.currentQuality = newQuality;
        this.applyAdaptiveConfig(newQuality);
        this.lastConfigChangeTime = now;

        // 发送质量变化事件
        eventBus.emit('network:qualityChanged', {
          quality: newQuality,
          metrics: { rtt: avgRtt, packetLoss: avgLoss, jitter: avgJitter },
          config: this.lastConfig,
        });
      }
    }

    // 即使 quality 没变，也要持续走 fps 平滑：
    // applyAdaptiveConfig 只在 quality 切换时调用一次，
    // 而"慢降"需要持续 1Hz 触发才能从 30 一步步降到 10。
    this.evaluateFpsSmoothingTick();
  }

  /**
   * fps 平滑步进 tick（每 1Hz 触发，独立于 quality 切换）
   * 仅当 smoothedFps 与 lastSentFps 不同时才发送 video_config（去重）
   */
  private evaluateFpsSmoothingTick(): void {
    if (!this.lastConfig) return;
    if (this.smoothedFps === null) return;  // 首次 applyAdaptiveConfig 才会初始化
    if (this.dataChannel?.readyState !== 'open') return;

    const target = this.lastConfig.fps;
    const next = this.smoothFps(target);
    if (next === this.lastSentFps) return;  // 与上次发送相同，跳过

    this.dataChannel.send(JSON.stringify({
      type: 'video_config',
      fps: next,
      bitrate: this.lastConfig.maxBitrate,
    }));
    this.lastSentFps = next;
    // [DEBUG-PROBE TODO: delete] [VideoConfigSent] tick 路径节奏跟踪
    const now = Date.now();
    const intervalMs = this.lastVideoConfigSentMs > 0 ? now - this.lastVideoConfigSentMs : -1;
    this.lastVideoConfigSentMs = now;
    this.videoConfigSentCount++;
  }

  /**
   * 评估网络质量等级
   */
  private evaluateQuality(rtt: number, loss: number, jitter: number): NetworkQuality {
    if (rtt < QUALITY_THRESHOLDS.excellent.maxRtt &&
        loss < QUALITY_THRESHOLDS.excellent.maxLoss &&
        jitter < QUALITY_THRESHOLDS.excellent.maxJitter) {
      return NetworkQuality.EXCELLENT;
    }

    if (rtt < QUALITY_THRESHOLDS.good.maxRtt &&
        loss < QUALITY_THRESHOLDS.good.maxLoss &&
        jitter < QUALITY_THRESHOLDS.good.maxJitter) {
      return NetworkQuality.GOOD;
    }

    if (rtt < QUALITY_THRESHOLDS.fair.maxRtt &&
        loss < QUALITY_THRESHOLDS.fair.maxLoss &&
        jitter < QUALITY_THRESHOLDS.fair.maxJitter) {
      return NetworkQuality.FAIR;
    }

    if (rtt < QUALITY_THRESHOLDS.poor.maxRtt &&
        loss < QUALITY_THRESHOLDS.poor.maxLoss &&
        jitter < QUALITY_THRESHOLDS.poor.maxJitter) {
      return NetworkQuality.POOR;
    }

    return NetworkQuality.BAD;
  }

  /**
   * fps 平滑：快升慢降
   *   target ≥ smoothed → 立即采用（恢复时不延迟）
   *   target < smoothed → 每 FPS_DOWN_INTERVAL_MS 最多降 FPS_DOWN_STEP，且不低于 target
   * 启动初值：首次 = target，避免"启动期慢升"导致初始体验不佳
   *
   * 例：30→10 全程需要 7 步（30→27→24→21→18→15→12→10），约 7 秒
   */
  private smoothFps(targetFps: number): number {
    const now = Date.now();

    if (this.smoothedFps === null) {
      this.smoothedFps = targetFps;
      this.lastFpsEvalMs = now;
      return targetFps;
    }

    if (targetFps >= this.smoothedFps) {
      // 快升：立即跟随
      if (targetFps !== this.smoothedFps) {
      }
      this.smoothedFps = targetFps;
      this.lastFpsEvalMs = now;
      return targetFps;
    }

    // 慢降：冷却中保持当前值
    if (now - this.lastFpsEvalMs < this.FPS_DOWN_INTERVAL_MS) {
      return this.smoothedFps;
    }

    const next = Math.max(targetFps, this.smoothedFps - this.FPS_DOWN_STEP);
    if (next !== this.smoothedFps) {
    }
    this.smoothedFps = next;
    this.lastFpsEvalMs = now;
    return next;
  }

  /**
   * 应用自适应配置
   */
  private applyAdaptiveConfig(quality: NetworkQuality): void {
    const config = QUALITY_CONFIGS[quality];
    this.lastConfig = config;

    // fps 经过快升慢降平滑后再下发，避免档位切换造成的剧烈跳变
    const fpsToSend = this.smoothFps(config.fps);

    // 发送配置到 Host
    if (this.dataChannel?.readyState === 'open') {
      // 发送视频配置
      this.dataChannel.send(JSON.stringify({
        type: 'video_config',
        fps: fpsToSend,
        bitrate: config.maxBitrate,
      }));
      this.lastSentFps = fpsToSend;  // 与 evaluateFpsSmoothingTick 共享去重状态

      // [DEBUG-PROBE TODO: delete] [VideoConfigSent] qualityChange 路径节奏跟踪
      const now = Date.now();
      const intervalMs = this.lastVideoConfigSentMs > 0 ? now - this.lastVideoConfigSentMs : -1;
      this.lastVideoConfigSentMs = now;
      this.videoConfigSentCount++;

      // 发送 FEC 配置 (如果 SDP 支持)
      if (this.sdpCapabilities?.fec || this.sdpCapabilities?.flexfec) {
        this.dataChannel.send(JSON.stringify({
          type: 'fec_config',
          enabled: config.fecEnabled,
        }));
      }

    }

    // 通过 RTCRtpSender 设置参数 (浏览器端优化)
    // 注意: 浏览器端优化用 fpsToSend 而非 config.fps，保持平滑后的帧率上限一致
    this.applyBrowserSideOptimizations({ ...config, fps: fpsToSend });
  }

  /**
   * 应用浏览器端优化
   */
  private async applyBrowserSideOptimizations(config: AdaptiveConfig): Promise<void> {
    if (!this.pc) return;

    // 获取视频接收器并设置参数
    const receivers = this.pc.getReceivers();
    for (const receiver of receivers) {
      if (receiver.track?.kind === 'video') {
        try {
          // 请求降低带宽时的关键帧
          // @ts-ignore - 实验性 API
          if (receiver.playoutDelayHint !== undefined) {
            // 根据网络质量调整播放延迟
            const delayHint = config.qualityPreset === 'low' ? 0.2 : 0.05;
            // @ts-ignore
            receiver.playoutDelayHint = delayHint;
          }
        } catch (e) {
          // 忽略不支持的 API
        }
      }
    }
  }

  /**
   * 触发 ICE Restart
   */
  private async triggerIceRestart(): Promise<void> {
    if (!this.pc) return;

    this.iceDisconnectTime = 0;

    try {
      // 创建新的 offer 并设置 iceRestart
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);

      // 通知信令服务器发送新的 offer
      eventBus.emit('webrtc:iceRestart', {
        sdp: offer.sdp,
        type: offer.type,
      });

    } catch (e) {
      console.error('[NetworkAdaptive] ICE Restart failed:', e);
    }
  }

  /**
   * 计算数组平均值
   */
  private getAverage(arr: number[]): number {
    if (arr.length === 0) return 0;
    return arr.reduce((a, b) => a + b, 0) / arr.length;
  }

  /**
   * 获取当前网络质量
   */
  getQuality(): NetworkQuality {
    return this.currentQuality;
  }

  /**
   * 获取 SDP 能力
   */
  getCapabilities(): SdpCapabilities | null {
    return this.sdpCapabilities;
  }

  /**
   * 手动强制应用配置
   */
  forceConfig(quality: NetworkQuality): void {
    this.currentQuality = quality;
    this.applyAdaptiveConfig(quality);
  }

  /**
   * 启用/禁用自适应
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }
}

export const networkAdaptiveService = new NetworkAdaptiveService();
